import React, { FC, InputHTMLAttributes } from 'react'
import styled from 'styled-components'

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  value: string
}

export const Radio: FC<Props> = ({ label, onChange, ...restProps }) => {
  return (
    <RadioLabel>
      <RadioInput onChange={onChange} {...restProps} type={'radio'} />
      <Inner>
        <Field />
        <Text>{label}</Text>
      </Inner>
    </RadioLabel>
  )
}

const Inner = styled.span`
  display: flex;
  height: 100%;
  cursor: pointer;
`

const Field = styled.span`
  position: relative;

  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
  width: ${({ theme }) => theme.spacing[4]};
  height: ${({ theme }) => theme.spacing[4]};
  margin: 3px 0;
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.white};
  outline: none;

  transition-property: border-color, border-width, background-color;
  transition-duration: 250ms;
  transition-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
  transition-delay: 0ms, 250ms;

  &::before {
    content: '';

    z-index: 1;
    position: absolute;
    top: 50%;
    left: 50%;

    width: ${({ theme }) => theme.spacing[2]};
    height: ${({ theme }) => theme.spacing[2]};
    border-radius: 50%;
    background-color: transparent;

    transform: translate(-50%, -50%);
    transition: background-color 250ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }
`

const Text = styled.span`
  ${({ theme }) => theme.textStyles.p1};
  display: inline-block;
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.spacing[4]};

  color: ${({ theme }) => theme.palette.grey[900]};

  transition: color 250ms cubic-bezier(0.55, 0.055, 0.675, 0.19);

  &:empty {
    margin-left: 0;
  }
`

const RadioInput = styled.input`
  z-index: -1;
  position: absolute;
  opacity: 0;

  &:checked {
    & + ${Inner} {
      ${Field} {
        border-color: ${({ theme }) => theme.palette.brand.primary};
        border-width: ${({ theme }) => theme.spacing[2]};
        transition-delay: 0ms;

        &::before {
          background-color: ${({ theme }) => theme.palette.white};
          transition-delay: 250ms;
        }
      }
    }
  }

  &:hover,
  &:focus {
    & + ${Inner} {
      ${Field} {
        border-color: ${({ theme }) => theme.palette.brand.primary};
      }
    }
  }

  &[disabled] {
    pointer-events: none;

    &:checked {
      & + ${Inner} {
        ${Field} {
          border-color: ${({ theme }) => theme.palette.grey[200]};
          background-color: ${({ theme }) => theme.palette.grey[200]};

          &::before {
            background-color: ${({ theme }) => theme.palette.grey[200]};
            transition-delay: 0ms;
          }
        }
      }
    }

    & + ${Inner} {
      cursor: default;

      ${Field} {
        border-color: ${({ theme }) => theme.palette.grey[200]};
        background-color: ${({ theme }) => theme.palette.grey[200]};
      }
    }
  }
`

const RadioLabel = styled.label`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: auto;
  height: auto;
  min-height: ${({ theme }) => theme.spacing[8]};

  &:not(:last-child) {
    margin-right: ${({ theme }) => theme.spacing[4]};
  }
`
